import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

/**
 * /set-password?token=...
 *
 * Consumes the claim token sent in the "You're in" welcome email
 * (for Stripe-fulfilled buyers and admin-granted placeholders) and
 * for the Forgot-password flow. On success it signs the user in and
 * drops them straight into their library.
 */
export default function SetPassword() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get("token") || "");
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    if (!token) return toast.error("Missing reset link — check your email again");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      localStorage.setItem("lg_token", data.token);
      setUser(data.user);
      toast.success("Signed in. Welcome.");
      navigate("/library");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link expired or invalid. Ask for a new one below.");
    } finally {
      setSubmitting(false);
    }
  }

  if (token === null) {
    return (
      <Layout>
        <div className="container-narrow py-32 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-brand-inkMuted" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title="Set your password" description="Set the password for your South on Leadership account." noindex />
      <section className="container-narrow py-16 md:py-24 max-w-md">
        <div className="bg-white border border-brand-line p-8 md:p-10">
          <KeyRound className="w-8 h-8 text-brand-rust" />
          <h1 className="font-display text-3xl md:text-4xl mt-5 text-brand-ink leading-tight">
            Set your password
          </h1>
          <p className="mt-3 text-brand-inkMuted text-sm leading-relaxed">
            Choose a password. It signs you in on this browser and you can use it any time from the sign-in page.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="set-password-form">
            <div>
              <label className="overline text-brand-inkMuted">New password</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full bg-transparent border-b border-brand-line py-3 mt-1 text-brand-ink focus:outline-none focus:border-brand-rust"
                data-testid="set-password-new"
              />
            </div>
            <div>
              <label className="overline text-brand-inkMuted">Confirm password</label>
              <input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-transparent border-b border-brand-line py-3 mt-1 text-brand-ink focus:outline-none focus:border-brand-rust"
                data-testid="set-password-confirm"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="set-password-submit"
              className="w-full bg-brand-ink text-white hover:bg-black px-7 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
            >
              {submitting ? "Setting…" : "Set password & sign in"}
            </button>
          </form>

          <p className="mt-6 text-xs text-brand-inkMuted text-center">
            Link expired?{" "}
            <Link
              to="/forgot-password"
              data-testid="set-password-request-new"
              className="text-brand-rust hover:text-brand-ink underline underline-offset-4"
            >
              Send a fresh one
            </Link>
          </p>
        </div>
      </section>
    </Layout>
  );
}
